import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { createApi } from '@reduxjs/toolkit/query/react';
import { FINGERPRINT } from 'store/constants';
import { createBaseQueryWithPwPrefix } from 'store/middleware/pwContextMiddleware';
import type {
  KycVerificationStatus,
  PayHubCashierContractsPaymentsSubmitPaymentGroupRequest,
  PayHubCashierContractsUserCardsResponse,
  PayHubWidgetContractsRecurringCheckResponse,
  PayHubWidgetContractsSubmitPaymentResponse,
  PayHubWidgetContractsSubmitPayoutResponse,
} from 'store/models';
import { getSubmitCheckoutUrl, softLocalStorageGet } from 'utils';
import { WIDGET_API } from 'utils/constants';

import type { FlowTypes } from '../constants';

export interface CustomServerError {
  status: Pick<FetchBaseQueryError, 'status'> | string | number;
  errorMessage?: string;
  errorCode?: number;
}

type ExtendedFetchBaseQueryError = FetchBaseQueryError & {
  data?: {
    error_message: string;
    error_code: number;
  };
};

interface CheckoutSubmitRequestProps {
  flowType: FlowTypes;
  paygroupId: string;
  requestBody: PayHubCashierContractsPaymentsSubmitPaymentGroupRequest;
}

interface CardVerificationRequest {
  cardId?: number;
  cardPan?: string;
  file: File;
  saveCard?: boolean;
}

interface CardVerificationResponse {
  card_id: number;
  kyc_verification_status: KycVerificationStatus;
}

const CARDS_TAG = 'user_cards';

export const checkoutAPI = createApi({
  reducerPath: 'checkoutAPI',
  tagTypes: [CARDS_TAG],
  baseQuery: createBaseQueryWithPwPrefix(WIDGET_API),
  endpoints(builder) {
    return {
      submitCheckout: builder.query<
        PayHubWidgetContractsSubmitPaymentResponse | PayHubWidgetContractsSubmitPayoutResponse,
        CheckoutSubmitRequestProps
      >({
        query: ({ flowType, paygroupId, requestBody }) => ({
          url: getSubmitCheckoutUrl(flowType, paygroupId),
          method: 'POST',
          body: requestBody,
        }),
        transformErrorResponse: (response: ExtendedFetchBaseQueryError): CustomServerError => ({
          status: response?.status,
          errorMessage: response?.data?.error_message,
          errorCode: response?.data?.error_code,
        }),
      }),
      getUserCards: builder.query<PayHubCashierContractsUserCardsResponse, { payGroupId?: number; amount: number }>({
        query: ({ payGroupId, amount }) => {
          const fingerprint = softLocalStorageGet(FINGERPRINT);
          let headers = {};
          if (fingerprint) {
            headers = {
              'X-Fingerprint': fingerprint,
            };
          }
          const base = `/user-cards/${amount}`;
          const url = payGroupId ? `${base}?pay_group_id=${payGroupId}` : base;
          return {
            url,
            method: 'GET',
            headers,
          };
        },
        providesTags: [CARDS_TAG],
      }),
      deleteUserCard: builder.mutation<{ success: boolean; cardId: number }, number>({
        query(cardId) {
          return {
            url: '/user-cards',
            method: 'DELETE',
            body: {
              card_id: cardId,
              withholding_type: 'Payment',
            },
          };
        },
        invalidatesTags: [CARDS_TAG],
      }),
      recurringCheck: builder.query<PayHubWidgetContractsRecurringCheckResponse, { cardId: number; amount: number }>({
        query: ({ cardId, amount }) => ({
          url: '/user-cards/recurring-check',
          method: 'POST',
          body: {
            user_card_id: cardId,
            amount,
          },
        }),
      }),
      cardVerification: builder.mutation<CardVerificationResponse, CardVerificationRequest>({
        query: ({ cardId, cardPan, file, saveCard }) => {
          const formData = new FormData();
          if (cardId) {
            formData.append('card_id', String(cardId));
          }
          if (cardPan) {
            formData.append('card_pan', cardPan);
          }
          if (saveCard !== undefined) {
            formData.append('save_card', String(saveCard));
          }
          formData.append('file', file);
          return {
            url: '/user-card/kyc-verify',
            method: 'POST',
            body: formData,
            formData: true,
          };
        },
      }),
    };
  },
});

export const {
  useSubmitCheckoutQuery,
  useLazyGetUserCardsQuery,
  useDeleteUserCardMutation,
  useLazyRecurringCheckQuery,
} = checkoutAPI;

export const useCardVerificationMutation = checkoutAPI.endpoints.cardVerification.useMutation;

export default checkoutAPI.reducer;
